import { onCLS, onFCP, onLCP, onTTFB } from 'web-vitals'
import type { Metric } from 'web-vitals'

/**
 * Simple Web Vitals monitoring setup
 *
 * This provides basic performance monitoring for Core Web Vitals.
 * For production, consider integrating with analytics services.
 */

interface PerformanceMetric extends Metric {
  rating: 'good' | 'needs-improvement' | 'poor'
}

class SimplePerformanceMonitor {
  private metrics: Array<PerformanceMetric> = []

  init() {
    // Core Web Vitals monitoring
    onCLS((metric: Metric) => this.handleMetric(metric as PerformanceMetric))
    onFCP((metric: Metric) => this.handleMetric(metric as PerformanceMetric))
    onLCP((metric: Metric) => this.handleMetric(metric as PerformanceMetric))
    onTTFB((metric: Metric) => this.handleMetric(metric as PerformanceMetric))

    if (import.meta.env.DEV) {
      console.log('Performance monitoring initialized')
    }
  }

  private handleMetric(metric: PerformanceMetric) {
    // Store the metric
    this.metrics.push(metric)

    // Log to console with color coding based on rating
    const emoji =
      metric.rating === 'good'
        ? '✅'
        : metric.rating === 'needs-improvement'
          ? '⚠️'
          : '❌'

    if (import.meta.env.DEV) {
      console.log(
        `${emoji} ${metric.name}: ${metric.value.toFixed(2)}${metric.name === 'CLS' ? '' : 'ms'} (${metric.rating})`,
      )
      if (metric.rating === 'needs-improvement' || metric.rating === 'poor') {
        console.log(
          `${metric.name} Optimization Tip: ${this.getSuggestion(metric.name)}`,
        )
      }
    }

    // Alert on poor performance
    if (metric.rating === 'poor') {
      this.alertPoorPerformance(metric)
    }

    // Send to analytics in production
    if (import.meta.env.PROD) {
      this.sendToAnalytics(metric)
    }
  }

  private alertPoorPerformance(metric: PerformanceMetric) {
    if (import.meta.env.DEV) {
      console.warn(`Poor performance detected for ${metric.name}:`, {
        value: metric.value,
        threshold: this.getThreshold(metric.name),
        suggestion: this.getSuggestion(metric.name),
      })
    }
  }

  private getThreshold(metricName: string): string {
    const thresholds = {
      CLS: '0.1',
      FCP: '1.8s',
      FID: '100ms',
      LCP: '2.5s',
      TTFB: '800ms',
    }
    return thresholds[metricName as keyof typeof thresholds] || 'Unknown'
  }

  private getSuggestion(metricName: string): string {
    const suggestions = {
      CLS: 'Reduce layout shifts - reserve space for images, ads, and dynamic content',
      FCP: 'Optimize critical rendering path - minimize CSS, inline critical styles',
      FID: 'Reduce JavaScript execution time - use code splitting and web workers',
      LCP: 'Optimize largest contentful element - compress images, preload resources',
      TTFB: 'Improve server response time - optimize backend, use CDN',
    }
    return (
      suggestions[metricName as keyof typeof suggestions] ||
      'Check performance best practices'
    )
  }

  private sendToAnalytics(_metric: PerformanceMetric) {
    // Example: Send to your analytics endpoint
    // fetch('/api/analytics/performance', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     metric: metric.name,
    //     value: metric.value,
    //     rating: metric.rating,
    //     url: window.location.pathname,
    //     timestamp: Date.now()
    //   })
    // }).catch(console.error)
    // Example: Send to Google Analytics 4
    // if (typeof gtag !== 'undefined') {
    //   gtag('event', metric.name, {
    //     event_category: 'Web Vitals',
    //     event_label: metric.id,
    //     value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    //     non_interaction: true,
    //   })
    // }
  }

  getMetrics() {
    return this.metrics
  }

  getLatestMetrics() {
    const latest: Record<string, PerformanceMetric> = {}

    this.metrics.forEach((metric) => {
      const existing = latest[metric.name]
      if (!(metric.name in latest) || metric.value > existing.value) {
        latest[metric.name] = metric
      }
    })

    return Object.values(latest)
  }

  getSummary() {
    const latest = this.getLatestMetrics()

    return {
      totalMetrics: this.metrics.length,
      uniqueMetrics: latest.length,
      metrics: latest.map((metric) => ({
        name: metric.name,
        value: metric.value,
        rating: metric.rating,
        unit: metric.name === 'CLS' ? 'ratio' : 'ms',
      })),
      overallHealth: this.calculateOverallHealth(latest),
    }
  }

  private calculateOverallHealth(
    metrics: Array<PerformanceMetric>,
  ): 'good' | 'needs-improvement' | 'poor' {
    if (metrics.length === 0) return 'good'

    const ratings = metrics.map((m) => m.rating)
    const poorCount = ratings.filter((r) => r === 'poor').length
    const needsImprovementCount = ratings.filter(
      (r) => r === 'needs-improvement',
    ).length

    if (poorCount > 0) return 'poor'
    if (needsImprovementCount > ratings.length / 2) return 'needs-improvement'
    return 'good'
  }
}

export const performanceMonitor = new SimplePerformanceMonitor()

// Make available globally in development
if (import.meta.env.DEV) {
  // @ts-expect-error - Adding to window for debugging
  window.performanceMonitor = performanceMonitor
}

/**
 * Initialize performance monitoring
 * Call this in your main.tsx
 */
export function initPerformanceMonitoring() {
  performanceMonitor.init()
}
